// Channel sidebar — second column. Two modes:
//
//   - `home`  : Friends pseudo-row + DM list. The Friends row
//               is a single button at the top; the rest is the
//               DM conversation list with last-activity preview.
//
//   - `space` : Active Space's name + channel list, grouped by
//               channel type. Personal Workspaces include an
//               "Overview" row at the very top that takes the
//               user to the dashboard view.
//
// Both modes end with the bottom user card (`UserCard`).

import {
  Ban,
  ChevronDown,
  ChevronRight,
  Copy,
  FileCode,
  Files,
  Gauge,
  Hash,
  Inbox,
  Link2,
  Lock,
  MessageCircleOff,
  MoreHorizontal,
  Pencil,
  Phone,
  Pin,
  PinOff,
  Plus,
  Server,
  Settings,
  Trash2,
  UserRound,
  UserMinus,
  UserPlus,
  Users,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  GlassMaterial,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Label,
  ScrollArea,
  cn,
} from "@link/ui";
import { ROLE_PERMISSIONS } from "@link/shared";

import type {
  ChannelKind,
  ChannelView,
  DmView,
  FriendView,
  HomeTab,
  SpaceView,
} from "../types/ui";
import { moveItem } from "../lib/reorder";
import { api, type Space } from "../lib/api";
import { renderForSurface } from "../lib/secrets-placeholder";
import { ignoreAccount } from "../lib/ignored";
import { openAfterOverlayDismiss } from "../lib/overlay-transition";
import { useMediaSession } from "../state/MediaSession";
import { useSelf } from "../state/SelfSession";
import { UserCard } from "./UserCard";
import { StatusDot } from "./StatusDot";
import { ServerInviteDialog } from "./ServerInviteDialog";
import { ServerSettingsModal } from "./ServerSettingsModal";
import { UserProfileModal } from "./UserProfileModal";
import { BadgeRow } from "./BadgeRow";
import type { MemberProfile } from "./MemberProfilePopover";
import { verifiedBadgeOnly } from "../lib/badges";

type Props =
  | {
      mode: "home";
      homeTab: HomeTab;
      onSelectHomeTab: (tab: HomeTab) => void;
      dms: DmView[];
      friends: FriendView[];
      friendCount: number;
      activeDmXid: string | null;
      onSelectDm: (xid: string) => void;
      onDmsChanged: () => void;
      onFriendsChanged: () => void;
      onDmClosed: (xid: string) => void;
      onGroupCreated: (xid: string) => void;
      mobile?: boolean;
    }
  | {
      mode: "space";
      space: SpaceView;
      channels: ChannelView[];
      activeChannelXid: string | undefined;
      onSelectChannel: (xid: string) => void;
      onSpaceChanged: () => void;
      onSpaceDeleted: () => void;
      mobile?: boolean;
    };

const TYPE_ORDER: Record<ChannelKind, number> = {
  overview: -1,
  text: 0,
  snippets: 1,
  files: 2,
  secrets: 3,
};

const TYPE_HEADING: Record<ChannelKind, string> = {
  overview: "Workspace",
  text: "Text channels",
  snippets: "Snippets",
  files: "Files",
  secrets: "Secrets",
};

const TYPE_ICON: Record<ChannelKind, ReactNode> = {
  overview: <Gauge className="h-3.5 w-3.5" />,
  text: <Hash className="h-3.5 w-3.5" />,
  snippets: <FileCode className="h-3.5 w-3.5" />,
  files: <Files className="h-3.5 w-3.5" />,
  secrets: <Lock className="h-3.5 w-3.5" />,
};

const ROW_ICON: Record<ChannelKind, ReactNode> = {
  overview: <Gauge className="h-4 w-4" />,
  text: <Hash className="h-4 w-4" />,
  snippets: <FileCode className="h-4 w-4" />,
  files: <Files className="h-4 w-4" />,
  secrets: <Lock className="h-4 w-4" />,
};

export function ChannelSidebar(props: Props) {
  if (props.mode === "home") {
    return <HomeSidebar {...props} />;
  }
  return <SpaceSidebar {...props} />;
}

// ---------------------------------------------------------------------------
// Home (Friends + DM) sidebar
// ---------------------------------------------------------------------------

function HomeSidebar(props: Extract<Props, { mode: "home" }>) {
  const media = useMediaSession();
  const [contextMenu, setContextMenu] = useState<{
    dm: DmView;
    x: number;
    y: number;
  } | null>(null);
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [groupOpen, setGroupOpen] = useState(false);

  useEffect(() => {
    if (contextMenu === null) return;
    const close = (): void => setContextMenu(null);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [contextMenu]);

  return (
    <aside
      className={cn(
        "glass-panel flex flex-col border-r border-cloud-200 bg-cloud-50",
        props.mobile ? "min-w-0 flex-1 md:w-72 md:flex-none" : "w-72",
      )}
    >
      <header className="flex items-center justify-between border-b border-cloud-200 px-3 py-3">
        <p className="text-base font-semibold text-cloud-800">Direct Messages</p>
        <button
          type="button"
          onClick={() => setGroupOpen(true)}
          className="flex h-7 w-7 items-center justify-center rounded-md text-cloud-500 hover:bg-strawberry-50 hover:text-strawberry-600"
          title="Create group chat"
        >
          <Plus className="h-4 w-4" />
        </button>
      </header>

      <div className="px-2 pt-2 pb-1 space-y-0.5">
        <button
          type="button"
          onClick={() => props.onSelectHomeTab("friends")}
          aria-current={props.homeTab === "friends" ? "page" : undefined}
          className={cn(
            "liquid-nav-item flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
            props.homeTab === "friends"
              ? "bg-strawberry-gradient text-white shadow-sm"
              : "text-cloud-700 hover:bg-strawberry-50 hover:text-strawberry-700",
          )}
        >
          <Users className="h-4 w-4" />
          <span className="font-medium">Friends</span>
        </button>
        <button
          type="button"
          onClick={() => props.onSelectHomeTab("inbox")}
          aria-current={props.homeTab === "inbox" ? "page" : undefined}
          className={cn("liquid-nav-item flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors", props.homeTab === "inbox" ? "bg-strawberry-gradient text-white shadow-sm" : "text-cloud-700 hover:bg-strawberry-50 hover:text-strawberry-700")}
        >
          <Inbox className="h-4 w-4" />
          <span className="font-medium">Inbox</span>
        </button>
        <button
          type="button"
          onClick={() => { props.onSelectHomeTab("friends"); window.dispatchEvent(new Event("link:open-add-friend")); }}
          className="liquid-nav-item flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-cloud-700 transition-colors hover:bg-strawberry-50 hover:text-strawberry-700"
        >
          <UserPlus className="h-4 w-4" />
          <span className="font-medium">Add Friend</span>
        </button>
      </div>

      <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-cloud-500">
        Direct messages
      </div>

      <ScrollArea className="flex-1">
        <ul className="space-y-0.5 px-2 pb-2">
          {props.dms.map((dm) => {
            const active = props.homeTab === "dm" && props.activeDmXid === dm.xid;
            const initials = dm.name.slice(0, 2).toUpperCase();
            return (
              <li key={dm.xid}>
                <button
                  type="button"
                  onClick={() => props.onSelectDm(dm.xid)}
                  aria-current={active ? "page" : undefined}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    setContextMenu({ dm, x: event.clientX, y: event.clientY });
                  }}
                  className={cn(
                    "liquid-nav-item flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                    active
                      ? "bg-strawberry-100 text-strawberry-800"
                      : "text-cloud-700 hover:bg-strawberry-50 hover:text-strawberry-700",
                  )}
                >
                  <div className="relative flex-shrink-0">
                    <Avatar className="h-7 w-7">
                      {dm.participants[0]?.avatarUrl != null ? (
                        <AvatarImage src={dm.participants[0].avatarUrl} alt="" />
                      ) : null}
                      <AvatarFallback variant={dm.isGroup ? "initials" : "ghost"} className="text-[11px]">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    {!dm.isGroup && dm.participants[0] !== undefined ? (
                      <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-px">
                        <StatusDot
                          status={dm.participants[0].status}
                          sizeClass="h-2.5 w-2.5"
                          ringClass="ring-white"
                        />
                      </span>
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1 truncate font-medium leading-tight">
                      <span className="truncate">{dm.name}</span>
                      <BadgeRow
                        badges={verifiedBadgeOnly(dm.participants[0]?.badges ?? [])}
                        max={1}
                      />
                    </p>
                    <p className="truncate text-[11px] text-cloud-400">
                      {
                        renderForSurface({
                          kind: dm.previewKind ?? "text",
                          surface: "channel-preview",
                          body: dm.preview,
                        }).display
                      }
                    </p>
                  </div>
                  {!active && dm.unreadCount > 0 ? (
                    <span
                      className="ml-auto flex min-h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-black leading-none text-white shadow-sm"
                      aria-label={`${dm.unreadCount} unread ${dm.unreadCount === 1 ? "message" : "messages"}`}
                    >
                      {dm.unreadCount > 99 ? "99+" : dm.unreadCount}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </ScrollArea>

      <UserCard />
      {contextMenu !== null ? (
        <DmContextMenu
          dm={contextMenu.dm}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onChanged={props.onDmsChanged}
          onFriendsChanged={props.onFriendsChanged}
          onClosed={() => props.onDmClosed(contextMenu.dm.xid)}
          onProfile={() => {
            const participant = contextMenu.dm.participants[0];
            if (participant !== undefined) setProfile(friendToProfile(participant));
            setContextMenu(null);
          }}
          onCall={() => {
            const dm = contextMenu.dm;
            setContextMenu(null);
            void media.startCall({ context: "dm", xid: dm.xid, label: `${dm.name} call` });
          }}
        />
      ) : null}
      {profile !== null ? (
        <UserProfileModal
          open
          member={profile}
          onOpenChange={(open) => {
            if (!open) setProfile(null);
          }}
        />
      ) : null}
      <CreateGroupDialog open={groupOpen} friends={props.friends} onOpenChange={setGroupOpen} onCreated={(xid) => { props.onDmsChanged(); props.onGroupCreated(xid); }} />
    </aside>
  );
}

function CreateGroupDialog({ open, friends, onOpenChange, onCreated }: { open: boolean; friends: FriendView[]; onOpenChange: (open: boolean) => void; onCreated: (xid: string) => void }) {
  const [selected, setSelected] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (!open) { setSelected([]); setName(""); setError(null); } }, [open]);
  const create = async (): Promise<void> => {
    if (busy) return;
    setBusy(true); setError(null);
    try { const dm = await api.dms.create({ participant_xids: selected, is_group: true, ...(name.trim() ? { name: name.trim() } : {}) }); onOpenChange(false); onCreated(dm.xid); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Couldn't create that group chat."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85dvh] max-w-md overflow-y-auto rounded-[24px] bg-app-canvas">
        <DialogTitle>Create a group chat</DialogTitle>
        <DialogDescription>Start with just you, or invite up to nine friends. You can add people later.</DialogDescription>
        <div className="space-y-4">
          <Input aria-label="Group name (optional)" value={name} disabled={busy} onChange={(event) => setName(event.target.value)} placeholder="Group name (optional)" maxLength={100} />
          <div className="max-h-72 space-y-1 overflow-y-auto rounded-2xl border border-cloud-200 bg-white p-2">
            {friends.map((friend) => {
              const checked = selected.includes(friend.xid);
              return (
                <button key={friend.xid} type="button" aria-pressed={checked} disabled={busy || (!checked && selected.length >= 9)}
                  onClick={() => setSelected((current) => checked ? current.filter((xid) => xid !== friend.xid) : [...current, friend.xid])}
                  className={cn("flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left disabled:opacity-50", checked ? "bg-strawberry-50 text-strawberry-800" : "hover:bg-cloud-50")}>
                  <Avatar className="h-9 w-9">{friend.avatarUrl != null ? <AvatarImage src={friend.avatarUrl} alt="" /> : null}<AvatarFallback>{friend.name.slice(0, 2)}</AvatarFallback></Avatar>
                  <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{friend.name}</span><span className="block truncate text-xs text-cloud-400">@{friend.username ?? friend.tag}</span></span>
                  <span aria-hidden="true" className={cn("h-5 w-5 rounded-md border", checked ? "border-strawberry-500 bg-strawberry-500" : "border-cloud-300")} />
                </button>
              );
            })}
            {friends.length === 0 ? <p className="p-6 text-center text-sm text-cloud-500">Your group can start with just you. Add friends whenever you're ready.</p> : null}
          </div>
          {selected.length === 0 ? <p className="text-sm text-cloud-600">Only you will be in this group for now.</p> : null}
          {error !== null ? <p role="alert" className="text-sm text-red-600">{error}</p> : null}
          <Button className="w-full" disabled={busy} onClick={() => void create()}>{busy ? "Creating…" : `Create group (${selected.length + 1}/10)`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DmContextMenu({
  dm,
  x,
  y,
  onClose,
  onChanged,
  onFriendsChanged,
  onClosed,
  onProfile,
  onCall,
}: {
  dm: DmView;
  x: number;
  y: number;
  onClose: () => void;
  onChanged: () => void;
  onFriendsChanged: () => void;
  onClosed: () => void;
  onProfile: () => void;
  onCall: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [serverChoices, setServerChoices] = useState<Space[] | null>(null);
  const participant = dm.isGroup ? undefined : dm.participants[0];
  const left = Math.max(8, Math.min(x, window.innerWidth - 264));
  const availableHeight = Math.max(0, window.innerHeight - 16);
  const menuHeight = Math.min(510, availableHeight);
  const top = Math.max(8, Math.min(y, window.innerHeight - menuHeight - 8));

  const run = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      await action();
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "That action couldn't be completed.");
    } finally {
      setBusy(false);
    }
  };

  const loadServers = async (): Promise<void> => {
    await run(async () => {
      const result = await api.spaces.list();
      setServerChoices(
        result.spaces.filter((space) => space.space_type !== "personal_workspace"),
      );
    });
  };

  const inviteTo = async (space: Space): Promise<void> => {
    await run(async () => {
      const invite = await api.spaces.createInvite(space.xid);
      await navigator.clipboard.writeText(
        `${window.location.origin}/invite/${invite.xid}`,
      );
      setMessage(`Invite to ${space.name} copied.`);
    });
  };

  return (
    <div
      role="menu"
      aria-label={`Direct message actions for ${dm.name}`}
      onMouseDown={(event) => event.stopPropagation()}
      className="fixed z-[230] max-h-[calc(100dvh-1rem)] w-64 overflow-y-auto overscroll-contain rounded-xl border border-white/10 bg-[#232428] p-1.5 text-sm text-white shadow-2xl"
      style={{ left, top }}
    >
      {serverChoices !== null ? (
        <>
          <DmMenuButton onClick={() => setServerChoices(null)}>
            <ChevronRight className="h-4 w-4 rotate-180" /> Back
          </DmMenuButton>
          <div className="my-1 h-px bg-white/10" />
          {serverChoices.length === 0 ? (
            <p className="px-2 py-3 text-xs text-white/55">You aren't in a server yet.</p>
          ) : (
            serverChoices.map((space) => (
              <DmMenuButton
                key={space.xid}
                disabled={busy}
                onClick={() => void inviteTo(space)}
              >
                <Server className="h-4 w-4" />
                <span className="truncate">{space.name}</span>
              </DmMenuButton>
            ))
          )}
        </>
      ) : (
        <>
          <DmMenuButton
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const result = await api.dms.messages(dm.xid, { limit: 1 });
                const latest = result.messages[0];
                if (latest !== undefined) {
                  await api.dms.setLastRead(dm.xid, {
                    last_read_message_xid: latest.xid,
                  });
                }
                setMessage("Marked as read.");
              })
            }
          >
            <MessageCircleOff className="h-4 w-4" /> Mark as read
          </DmMenuButton>
          <DmMenuButton
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.dms.updateSettings(dm.xid, { pinned: !dm.pinned });
                onChanged();
                onClose();
              })
            }
          >
            {dm.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
            {dm.pinned ? "Unpin" : "Pin"}
          </DmMenuButton>
          <div className="my-1 h-px bg-white/10" />
          {!dm.isGroup ? (
            <DmMenuButton onClick={onProfile}>
              <UserRound className="h-4 w-4" /> Profile
            </DmMenuButton>
          ) : null}
          <DmMenuButton onClick={onCall}>
            <Phone className="h-4 w-4" /> Start a call
          </DmMenuButton>
          <DmMenuButton disabled={busy} onClick={() => void loadServers()}>
            <Server className="h-4 w-4" />
            <span className="flex-1">Invite to server</span>
            <ChevronRight className="h-4 w-4" />
          </DmMenuButton>
          <DmMenuButton
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.dms.updateSettings(dm.xid, { hidden: true });
                onClosed();
                onClose();
              })
            }
          >
            <X className="h-4 w-4" /> Close DM
          </DmMenuButton>
          <div className="my-1 h-px bg-white/10" />
          {!dm.isGroup && participant !== undefined ? (
            <>
              <DmMenuButton
                onClick={() => {
                  ignoreAccount(participant.xid);
                  setMessage(`${participant.name} is now ignored on this device.`);
                }}
              >
                <UserMinus className="h-4 w-4" /> Ignore
              </DmMenuButton>
              <DmMenuButton
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    if (!window.confirm(`Remove ${participant.name} from your friends?`)) return;
                    await api.friends.remove(participant.xid);
                    onFriendsChanged();
                    setMessage(`${participant.name} was removed from your friends.`);
                  })
                }
              >
                <UserMinus className="h-4 w-4" /> Remove friend
              </DmMenuButton>
              <DmMenuButton
                danger
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    if (!window.confirm(`Block ${participant.name}?`)) return;
                    await api.friends.block({ account_xid: participant.xid });
                    onFriendsChanged();
                    onClosed();
                    onClose();
                  })
                }
              >
                <Ban className="h-4 w-4" /> Block
              </DmMenuButton>
            </>
          ) : null}
          <div className="my-1 h-px bg-white/10" />
          <DmMenuButton
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.dms.updateSettings(dm.xid, { muted: !dm.muted });
                onChanged();
                onClose();
              })
            }
          >
            {dm.muted ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            {dm.muted ? "Unmute conversation" : `Mute @${participant?.username ?? dm.name}`}
          </DmMenuButton>
        </>
      )}
      {message !== null ? (
        <p className="mt-1 rounded-lg bg-white/10 px-2 py-2 text-xs text-white/75">{message}</p>
      ) : null}
    </div>
  );
}

function DmMenuButton({
  children,
  danger = false,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors disabled:opacity-45",
        danger ? "text-red-400 hover:bg-red-950/35" : "hover:bg-white/10",
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function friendToProfile(friend: DmView["participants"][number]): MemberProfile {
  const memberSince = friend.memberSince === undefined
    ? undefined
    : new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(friend.memberSince));
  return {
    name: friend.name,
    tag: friend.xid,
    ...(friend.username !== undefined ? { username: friend.username } : {}),
    ...(friend.avatarUrl !== undefined ? { avatarUrl: friend.avatarUrl } : {}),
    ...(friend.bannerColor !== undefined ? { bannerColor: friend.bannerColor } : {}),
    ...(friend.bannerMode !== undefined ? { bannerMode: friend.bannerMode } : {}),
    ...(friend.bannerSecondaryColor !== undefined
      ? { bannerSecondaryColor: friend.bannerSecondaryColor }
      : {}),
    ...(friend.pronouns !== undefined ? { pronouns: friend.pronouns } : {}),
    status: friend.status,
    bio: friend.note,
    roles: [],
    mutualSpaces: [],
    badges: friend.badges,
    communityTag: friend.communityTag,
    ...(memberSince !== undefined ? { memberSince } : {}),
  };
}

// ---------------------------------------------------------------------------
// Space sidebar
// ---------------------------------------------------------------------------

function SpaceSidebar(props: Extract<Props, { mode: "space" }>) {
  const self = useSelf();
  const isOwner = props.space.ownerAccountXid === self.identity.accountXid;
  const [effectivePermissions, setEffectivePermissions] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [serverMenuOpen, setServerMenuOpen] = useState(false);
  const serverMenuRef = useRef<HTMLDivElement>(null);
  const [menuMessage, setMenuMessage] = useState<string | null>(null);
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [createChannelType, setCreateChannelType] = useState<Exclude<ChannelKind, "overview">>("text");
  const [channelMenu, setChannelMenu] = useState<{ channel: ChannelView; x: number; y: number } | null>(null);
  const [editChannel, setEditChannel] = useState<ChannelView | null>(null);
  // Local mirror of the channels prop. Drag-reorder writes here
  // optimistically; an effect debounces persistence.
  const [order, setOrder] = useState<ChannelView[]>(props.channels);
  const orderDirty = useRef(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (isOwner) {
      setEffectivePermissions(Number.MAX_SAFE_INTEGER);
      return undefined;
    }
    void Promise.all([
      api.spaces.members(props.space.xid),
      api.spaces.roles(props.space.xid),
    ]).then(([members, roles]) => {
      if (cancelled) return;
      const member = members.members.find((candidate) => candidate.account_id === self.identity.accountXid);
      if (member?.is_admin === true) {
        setEffectivePermissions(Number.MAX_SAFE_INTEGER);
        return;
      }
      const assigned = new Set(member?.roles.map((role) => role.xid) ?? []);
      setEffectivePermissions(roles.roles.reduce(
        (mask, role) => assigned.has(role.xid) ? mask | role.permissions : mask,
        roles.default_permissions,
      ));
    }).catch(() => {
      if (!cancelled) setEffectivePermissions(0);
    });
    return () => {
      cancelled = true;
    };
  }, [isOwner, props.space.xid, self.identity.accountXid]);
  const canInvite = isOwner || (effectivePermissions & ROLE_PERMISSIONS.create_invites) !== 0;
  const canManageChannels = isOwner || (effectivePermissions & ROLE_PERMISSIONS.manage_channels) !== 0;
  const canOpenSettings = isOwner || [
    ROLE_PERMISSIONS.manage_server,
    ROLE_PERMISSIONS.manage_roles,
    ROLE_PERMISSIONS.ban_members,
    ROLE_PERMISSIONS.manage_invites,
    ROLE_PERMISSIONS.view_audit_log,
    ROLE_PERMISSIONS.manage_expressions,
  ].some((permission) => (effectivePermissions & permission) !== 0);
  // When the upstream prop changes (different space), reset the
  // local order rather than carry stale rows over.
  useEffect(() => {
    orderDirty.current = false;
    setOrder(props.channels);
  }, [props.channels]);

  useEffect(() => {
    if (!orderDirty.current || !canManageChannels) return undefined;
    const t = window.setTimeout(() => {
      orderDirty.current = false;
      setOrderError(null);
      void Promise.all(
        order.map((channel, index) =>
          api.channels.patch(channel.xid, { sort_position: index }),
        ),
      ).catch((reason: unknown) => {
        setOrder(props.channels);
        setOrderError(
          reason instanceof Error
            ? reason.message
            : "Couldn't save the channel order.",
        );
      });
    }, 600);
    return () => window.clearTimeout(t);
  }, [canManageChannels, order, props.channels]);

  useEffect(() => {
    if (!serverMenuOpen) return undefined;

    const closeWhenOutside = (event: PointerEvent): void => {
      const menu = serverMenuRef.current;
      if (
        menu !== null &&
        event.target instanceof Node &&
        !menu.contains(event.target)
      ) {
        setServerMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setServerMenuOpen(false);
    };

    document.addEventListener("pointerdown", closeWhenOutside, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeWhenOutside, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [serverMenuOpen]);

  useEffect(() => {
    if (channelMenu === null) return undefined;
    const close = (event: PointerEvent): void => {
      if (event.target instanceof Element && event.target.closest("[data-channel-menu]") !== null) return;
      setChannelMenu(null);
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setChannelMenu(null);
    };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", escape);
    };
  }, [channelMenu]);

  const [dragXid, setDragXid] = useState<string | null>(null);

  const groups = new Map<ChannelKind, ChannelView[]>();
  for (const channel of order) {
    const list = groups.get(channel.channelType) ?? [];
    list.push(channel);
    groups.set(channel.channelType, list);
  }
  const orderedGroups = Array.from(groups.entries()).sort(
    ([a], [b]) => TYPE_ORDER[a] - TYPE_ORDER[b],
  );

  const onReorder = (
    type: ChannelKind,
    fromXid: string,
    toXid: string,
  ): void => {
    if (fromXid === toXid) return;
    const groupList = groups.get(type) ?? [];
    const fromIdx = groupList.findIndex((c) => c.xid === fromXid);
    const toIdx = groupList.findIndex((c) => c.xid === toXid);
    if (fromIdx === -1 || toIdx === -1) return;
    const reorderedGroup = moveItem(groupList, fromIdx, toIdx);

    // Rebuild the full order: replace the type group with the
    // reordered list, leave the others in place.
    const next: ChannelView[] = [];
    for (const ch of order) {
      if (ch.channelType === type) {
        const replacement = reorderedGroup.shift();
        if (replacement !== undefined) next.push(replacement);
      } else {
        next.push(ch);
      }
    }
    orderDirty.current = true;
    setOrderError(null);
    setOrder(next);
  };

  const openChannelCreator = (type: Exclude<ChannelKind, "overview"> = "text"): void => {
    setCreateChannelType(type);
    setCreateChannelOpen(true);
    setServerMenuOpen(false);
  };

  const openChannelMenu = (channel: ChannelView, x: number, y: number): void => {
    const availableHeight = Math.max(0, window.innerHeight - 16);
    const menuHeight = Math.min(430, availableHeight);
    setChannelMenu({
      channel,
      x: Math.max(8, Math.min(x, window.innerWidth - 248)),
      y: Math.max(8, Math.min(y, window.innerHeight - menuHeight - 8)),
    });
  };

  const deleteChannel = async (channel: ChannelView): Promise<void> => {
    if (!window.confirm(`Delete #${channel.name}? This removes it for everyone.`)) return;
    setChannelMenu(null);
    setOrderError(null);
    try {
      await api.channels.delete(channel.xid);
      const remaining = order.filter((item) => item.xid !== channel.xid);
      setOrder(remaining);
      if (props.activeChannelXid === channel.xid) {
        const replacement = remaining.find((item) => item.channelType !== "overview");
        if (replacement !== undefined) props.onSelectChannel(replacement.xid);
      }
      props.onSpaceChanged();
    } catch (reason) {
      setOrderError(reason instanceof Error ? reason.message : "Couldn't delete this channel.");
    }
  };

  return (
    <aside
      className={cn(
        "glass-panel flex flex-col border-r border-cloud-200 bg-cloud-50",
        props.mobile ? "min-w-0 flex-1 md:w-72 md:flex-none" : "w-72",
      )}
    >
      <header className="flex items-center justify-between border-b border-cloud-200 px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-cloud-800">
            {props.space.name}
          </p>
          <p className="text-[11px] uppercase tracking-wide text-cloud-400">
            {props.space.spaceType.replace("_", " ")}
          </p>
        </div>
        <div ref={serverMenuRef} className="relative">
          <button
            type="button"
            onClick={() => {
              setServerMenuOpen((open) => !open);
              setMenuMessage(null);
            }}
            className="flex h-7 w-7 items-center justify-center rounded-md text-cloud-500 hover:bg-cloud-200 hover:text-cloud-900"
            aria-label={`Open ${props.space.name} menu`}
            aria-expanded={serverMenuOpen}
            aria-haspopup="menu"
          >
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                serverMenuOpen && "rotate-180",
              )}
            />
          </button>
          {serverMenuOpen ? (
            <div
              role="menu"
              aria-label={`${props.space.name} menu`}
              className="liquid-popup liquid-lens-host absolute right-0 top-full z-[170] mt-1.5 max-h-[calc(100dvh-5rem)] w-64 overflow-y-auto overscroll-contain rounded-2xl border border-cloud-300 bg-white p-2 text-cloud-700 shadow-2xl"
            >
            <GlassMaterial radius={16} frosted />
            <p className="px-2 pb-1.5 pt-1 text-[10px] font-bold uppercase tracking-wider text-cloud-400">
              {props.space.name}
            </p>
            {canInvite ? <ServerMenuItem
              icon={<Link2 className="h-4 w-4" />}
              onClick={() => {
                setServerMenuOpen(false);
                setInviteOpen(true);
              }}
            >
              Invite people
            </ServerMenuItem> : null}
            {canOpenSettings || canManageChannels ? (
              <>
                {canOpenSettings ? <ServerMenuItem
                  icon={<Settings className="h-4 w-4" />}
                  onClick={() => {
                    openAfterOverlayDismiss(
                      () => setServerMenuOpen(false),
                      () => setSettingsOpen(true),
                    );
                  }}
                >
                  Server settings
                </ServerMenuItem> : null}
                <div className="my-1.5 h-px bg-cloud-200" />
                {canManageChannels ? <ServerMenuItem
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => openChannelCreator()}
                >
                  Create channel
                </ServerMenuItem> : null}
              </>
            ) : null}
            <div className="my-1.5 h-px bg-cloud-200" />
            <ServerMenuItem
              icon={<Copy className="h-4 w-4" />}
              onClick={() => {
                void navigator.clipboard.writeText(props.space.xid);
                setMenuMessage("Server ID copied.");
              }}
            >
              Copy server ID
            </ServerMenuItem>
            {menuMessage !== null ? (
              <p className="mx-1 mt-2 rounded-lg bg-cloud-100 px-2.5 py-2 text-xs text-cloud-600">
                {menuMessage}
              </p>
            ) : null}
            </div>
          ) : null}
        </div>
      </header>

      <ScrollArea className="flex-1">
        <div className="space-y-4 px-2 py-3">
          {orderedGroups.map(([type, list]) => (
            <section key={type}>
              <div className="flex items-center justify-between px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-cloud-500">
                <span className="flex items-center gap-1.5">
                  {TYPE_ICON[type]}
                  {TYPE_HEADING[type]}
                </span>
                {canManageChannels && type !== "overview" && type !== "secrets" && (
                  <button
                    type="button"
                    onClick={() => openChannelCreator(type)}
                    className="flex h-5 w-5 items-center justify-center rounded text-cloud-400 hover:bg-strawberry-50 hover:text-strawberry-600"
                    aria-label={`Create ${TYPE_HEADING[type].toLowerCase()}`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <ul className="mt-0.5 space-y-0.5">
                {list.map((channel) => {
                  const active = channel.xid === props.activeChannelXid;
                  const dragging = dragXid === channel.xid;
                  const draggable = canManageChannels && channel.channelType !== "overview";
                  return (
                    <li
                      key={channel.xid}
                      onContextMenu={(event) => {
                        if (channel.channelType === "overview") return;
                        event.preventDefault();
                        openChannelMenu(channel, event.clientX, event.clientY);
                      }}
                      draggable={draggable}
                      onDragStart={(e) => {
                        if (!draggable) return;
                        setDragXid(channel.xid);
                        e.dataTransfer.effectAllowed = "move";
                        // Avoid the ghost picking up the strawberry
                        // gradient — the row is opaque, the gradient
                        // would distort during drag.
                        e.dataTransfer.setData("text/plain", channel.xid);
                      }}
                      onDragEnd={() => setDragXid(null)}
                      onDragOver={(e) => {
                        if (
                          dragXid !== null &&
                          dragXid !== channel.xid &&
                          channel.channelType === type
                        ) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragXid !== null) {
                          onReorder(type, dragXid, channel.xid);
                          setDragXid(null);
                        }
                      }}
                      className={cn(
                        "transition-opacity",
                        dragging && "opacity-40",
                      )}
                    >
                      <div
                        data-selected={active || undefined}
                        className={cn(
                          "liquid-nav-item group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                          active
                            ? "bg-strawberry-gradient text-white shadow-sm"
                            : "text-cloud-600 hover:bg-strawberry-50 hover:text-strawberry-700",
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => props.onSelectChannel(channel.xid)}
                          aria-current={active ? "page" : undefined}
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        >
                        <span
                          className={cn(
                            "flex-shrink-0",
                            active
                              ? "text-inherit"
                              : "text-cloud-400 group-hover:text-strawberry-600",
                          )}
                        >
                          {ROW_ICON[channel.channelType]}
                        </span>
                        <span className="truncate">
                          {channel.channelType === "overview"
                            ? "Overview"
                            : channel.name}
                        </span>
                        </button>
                        {channel.channelType !== "overview" ? (
                          <button
                            type="button"
                            onClick={(event) => {
                              const rect = event.currentTarget.getBoundingClientRect();
                              openChannelMenu(channel, rect.right, rect.bottom + 4);
                            }}
                            className={cn(
                              "flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-opacity hover:bg-black/10",
                              active ? "text-inherit" : "text-cloud-500 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100",
                            )}
                            aria-label={`Open #${channel.name} channel menu`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </ScrollArea>

      {channelMenu !== null ? (
        <div
          data-channel-menu
          role="menu"
          className="liquid-popup liquid-lens-host fixed z-[230] max-h-[calc(100dvh-1rem)] w-60 overflow-y-auto overscroll-contain rounded-2xl border border-cloud-300 bg-white p-2 text-cloud-700 shadow-2xl"
          style={{ left: channelMenu.x, top: channelMenu.y }}
        >
          <GlassMaterial radius={16} frosted />
          <p className="px-3 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-cloud-400">
            #{channelMenu.channel.name}
          </p>
          <ServerMenuItem icon={<MessageCircleOff className="h-4 w-4" />} onClick={() => { setChannelMenu(null); setMenuMessage(`#${channelMenu.channel.name} marked as read.`); }}>Mark as read</ServerMenuItem>
          {canInvite ? <ServerMenuItem icon={<UserPlus className="h-4 w-4" />} onClick={() => { setChannelMenu(null); setInviteOpen(true); }}>Invite to channel</ServerMenuItem> : null}
          {canManageChannels ? <ServerMenuItem icon={<Pin className="h-4 w-4" />} onClick={() => {
            const selected = channelMenu.channel;
            orderDirty.current = true;
            setOrder((current) => [selected, ...current.filter((item) => item.xid !== selected.xid)]);
            setChannelMenu(null);
          }}>Pin channel to top</ServerMenuItem> : null}
          <ServerMenuItem icon={<Copy className="h-4 w-4" />} onClick={() => {
            void navigator.clipboard.writeText(`${window.location.origin}/?space=${encodeURIComponent(props.space.xid)}&channel=${encodeURIComponent(channelMenu.channel.xid)}`);
            setChannelMenu(null);
            setMenuMessage("Channel link copied.");
          }}>Copy link</ServerMenuItem>
          <ServerMenuItem icon={<VolumeX className="h-4 w-4" />} onClick={() => {
            const key = `link:muted-channels:${self.identity.accountXid}`;
            const muted = new Set<string>(JSON.parse(localStorage.getItem(key) ?? "[]") as string[]);
            muted.add(channelMenu.channel.xid);
            localStorage.setItem(key, JSON.stringify([...muted]));
            setChannelMenu(null);
            setMenuMessage(`#${channelMenu.channel.name} muted on this device.`);
          }}>Mute channel</ServerMenuItem>
          {canManageChannels ? <>
            <div className="my-1 h-px bg-cloud-200" />
            <ServerMenuItem icon={<Pencil className="h-4 w-4" />} onClick={() => { setEditChannel(channelMenu.channel); setChannelMenu(null); }}>Edit channel</ServerMenuItem>
            <ServerMenuItem icon={<Copy className="h-4 w-4" />} onClick={() => {
              const source = channelMenu.channel;
              setChannelMenu(null);
              void api.spaces.createChannel(props.space.xid, { name: `${source.name}-copy`.slice(0, 80), channel_type: source.channelType as Exclude<ChannelKind, "overview"> }).then((created) => setOrder((current) => [...current, { xid: created.xid, name: created.name, channelType: created.channel_type }])).catch((reason: unknown) => setOrderError(reason instanceof Error ? reason.message : "Couldn't duplicate this channel."));
            }}>Duplicate channel</ServerMenuItem>
            <ServerMenuItem icon={<Plus className="h-4 w-4" />} onClick={() => { setChannelMenu(null); openChannelCreator("text"); }}>Create text channel</ServerMenuItem>
            <button type="button" onClick={() => void deleteChannel(channelMenu.channel)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" />Delete channel</button>
          </> : null}
        </div>
      ) : null}

      {orderError !== null ? (
        <p className="mx-3 mb-2 rounded-lg border border-red-300/40 bg-red-950/20 px-3 py-2 text-xs text-red-500">
          {orderError}
        </p>
      ) : null}

      <UserCard />
      <ServerInviteDialog
        open={inviteOpen}
        space={props.space}
        onOpenChange={setInviteOpen}
      />
      <ServerSettingsModal
        open={settingsOpen}
        space={props.space}
        onOpenChange={setSettingsOpen}
        onChanged={props.onSpaceChanged}
        onDeleted={props.onSpaceDeleted}
      />
      <CreateChannelDialog
        open={createChannelOpen}
        initialType={createChannelType}
        spaceName={props.space.name}
        onOpenChange={setCreateChannelOpen}
        onCreate={async (name, type) => {
          const created = await api.spaces.createChannel(props.space.xid, {
            name,
            channel_type: type,
          });
          setOrder((current) => [
            ...current,
            {
              xid: created.xid,
              name: created.name,
              channelType: created.channel_type,
            },
          ]);
        }}
      />
      <EditChannelDialog
        channel={editChannel}
        onOpenChange={(open) => { if (!open) setEditChannel(null); }}
        onSave={async (channel, name) => {
          const updated = await api.channels.patch(channel.xid, { name });
          setOrder((current) => current.map((item) => item.xid === channel.xid ? { ...item, name: updated.name } : item));
          props.onSpaceChanged();
        }}
      />
    </aside>
  );
}

function ServerMenuItem({
  children,
  icon,
  disabled = false,
  onClick,
}: {
  children: ReactNode;
  icon: ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-cloud-700 transition-colors hover:bg-cloud-200/70 hover:text-cloud-900 disabled:cursor-wait disabled:opacity-50"
    >
      <span className="text-cloud-500">{icon}</span>
      {children}
    </button>
  );
}

function CreateChannelDialog({
  open,
  initialType,
  spaceName,
  onOpenChange,
  onCreate,
}: {
  open: boolean;
  initialType: Exclude<ChannelKind, "overview">;
  spaceName: string;
  onOpenChange: (open: boolean) => void;
  onCreate: (
    name: string,
    type: Exclude<ChannelKind, "overview">,
  ) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState(initialType);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setType(initialType);
    setError(null);
  }, [initialType, open]);

  if (!open) return null;

  const submit = async (): Promise<void> => {
    const normalized = name.trim().toLowerCase().replace(/\s+/g, "-");
    if (normalized.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(normalized, type);
      onOpenChange(false);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Couldn't create this channel.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-cloud-900/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl border border-cloud-300 bg-white p-6 text-cloud-700 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-cloud-900">Create a channel</h2>
            <p className="mt-1 text-sm text-cloud-500">Add a new place inside {spaceName}.</p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-xl p-2 text-cloud-400 hover:bg-cloud-100 hover:text-cloud-700"
            aria-label="Close channel creator"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-6 space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="new-channel-name">Channel name</Label>
            <Input
              id="new-channel-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void submit();
              }}
              maxLength={80}
              autoFocus
              placeholder="new-channel"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-channel-type">Channel type</Label>
            <select
              id="new-channel-type"
              value={type}
              onChange={(event) => setType(event.target.value as Exclude<ChannelKind, "overview">)}
              className="h-10 w-full rounded-xl border border-cloud-200 bg-white px-3 text-sm text-cloud-700 outline-none focus:ring-2 focus:ring-strawberry-300"
            >
              <option value="text">Text</option>
              <option value="snippets">Snippets</option>
              <option value="files">Files</option>
            </select>
          </div>
          {error !== null ? (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} disabled={busy || name.trim().length === 0}>
              {busy ? "Creating…" : "Create channel"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditChannelDialog({
  channel,
  onOpenChange,
  onSave,
}: {
  channel: ChannelView | null;
  onOpenChange: (open: boolean) => void;
  onSave: (channel: ChannelView, name: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(channel?.name ?? "");
    setError(null);
  }, [channel]);

  if (channel === null) return null;
  const submit = async (): Promise<void> => {
    const normalized = name.trim().toLowerCase().replace(/\s+/g, "-");
    if (normalized.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(channel, normalized);
      onOpenChange(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Couldn't update this channel.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-cloud-900/70 p-4 backdrop-blur-sm" onPointerDown={(event) => { if (event.target === event.currentTarget) onOpenChange(false); }}>
      <div className="w-full max-w-md rounded-3xl border border-cloud-300 bg-white p-6 text-cloud-700 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div><h2 className="text-xl font-bold text-cloud-900">Edit channel</h2><p className="mt-1 text-sm text-cloud-500">Rename #{channel.name} for everyone.</p></div>
          <button type="button" onClick={() => onOpenChange(false)} className="rounded-xl p-2 text-cloud-400 hover:bg-cloud-100" aria-label="Close channel editor"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-6 space-y-4">
          <FieldLabel htmlFor="edit-channel-name">Channel name</FieldLabel>
          <Input id="edit-channel-name" autoFocus value={name} onChange={(event) => setName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void submit(); }} maxLength={80} />
          {error !== null ? <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p> : null}
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button><Button onClick={() => void submit()} disabled={busy || name.trim().length === 0}>{busy ? "Saving…" : "Save changes"}</Button></div>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return <Label htmlFor={htmlFor}>{children}</Label>;
}
